/**
 * Data a tab will need, fetched ahead of its first visit (owner, 02.11): right after sign-in the app warms the
 * profile tab's reads in the background, and the tab's first open takes them instead of waiting on the network.
 *
 * Per client: an entry belongs to the customer it was fetched for and is never handed to another one; sign-out and a
 * client switch drop everything ({@link clearWarm}). An entry is taken once — a later mount reads fresh — and goes
 * stale after {@link MAX_AGE_MS}, so a tab opened long after sign-in still shows current data.
 */
const MAX_AGE_MS = 5 * 60_000;

interface Entry { clientId: string; at: number; value: Promise<unknown> }

const entries = new Map<string, Entry>();

/** Starts `load` for `clientId` now, unless a fresh entry for the same client is already there. */
export function warm<T>(clientId: string, key: string, load: () => Promise<T>): void {
  const e = entries.get(key);
  if (e && e.clientId === clientId && Date.now() - e.at < MAX_AGE_MS) return;
  const value = load();
  value.catch(() => { /* the taker sees the rejection and handles it */ });
  entries.set(key, { clientId, at: Date.now(), value });
}

/** The warmed result for `clientId` (consumed), or a fresh `load()` when there is none for this client. */
export function take<T>(clientId: string | null, key: string, load: () => Promise<T>): Promise<T> {
  const e = entries.get(key);
  entries.delete(key);
  if (e && clientId && e.clientId === clientId && Date.now() - e.at < MAX_AGE_MS) return e.value as Promise<T>;
  return load();
}

/** Drops every warmed entry — sign-out or another client signing in. */
export function clearWarm(): void {
  entries.clear();
}
